import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';
import RHFSelect from '../../controls/tempRHFControls/RHFSelect';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import DialogList from 'src/dialogs';
import { AnyObject } from 'final-form';

interface IReportOptionsDialogProps {
    dialogName: string;
    handleReport?: (category: string, subcategory: string) => void;
    handleBillingReport?: (data: AnyObject) => void | Promise<void>;
    hideDialog: () => void;
    showDialog: (dialogName: DialogList) => void;
    reportTypes?: Array<{ value: string | number, label: string }>;
    sortOptions?: Array<{ value: string | number, label: string }>;
    payPeriods?: Array<{value: number | string, label: string}>;
    // Debounced (see ClientProjectSearch.tsx's own payPeriodSearch pattern) - lets the parent widen
    // the fetched pay period list (e.g. a fresh OData query) when the typed term isn't found in the
    // already-loaded default set. Optional: without it, search is limited to filtering payPeriods.
    onPayPeriodSearch?: (searchTerm: string) => void;
    parentType?: number;
    offices?: Array<{value: number | string, label: string}>;
}

// Punctuation-insensitive match so "2026", "13", or "2026-13" all match a "(2026-13)" label.
const normalizePayPeriodInput = (str: string) => (str || '').replace(/[^0-9a-z]/gi, '').toLowerCase();

const ReportOptionsDialog = (props: IReportOptionsDialogProps) => {
    const [payPeriods, setPayPeriods] = useState<Array<{value: number | string, label: string}>>(undefined);
    const [isSearchingPayPeriods, setIsSearchingPayPeriods] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const { register, errors, getValues, reset, control } = useForm();

    useEffect(() => {
        onCreate();
    }, []);

    useEffect(() => {
        if (!!props?.payPeriods) {
            setPayPeriods(props?.payPeriods);
        }
    }, [props?.payPeriods]);

    // Mirrors ClientProjectSearch.tsx's payPeriodSearch - debounce keystrokes before asking the
    // parent for a wider result set (filterOption below already narrows the loaded list instantly).
    // The non-empty guard matters: react-select fires onInputChange('') when an option is selected
    // (clearing the input text), and without the guard that was re-triggering a fetch back to the
    // default +/-6 month window, wiping out a selection that came from outside it.
    const payPeriodSearch = _.debounce((inputText: string) => {
        if (!!inputText && inputText.length > 0 && props?.onPayPeriodSearch) {
            props.onPayPeriodSearch(inputText);
        }
    }, 500);

    const handlePayPeriodSearchChanged = (inputText: string) => {
        payPeriodSearch(inputText);
    };

    useEffect(() => {
        if (!isNaN(Number(props?.parentType)) && Number(props?.parentType) === 3) {
            reset({
                    reportOptions: {
                        reportType: '4',
                        wdFormat: '1'
                    }
                });
        }
    }, [props?.parentType]);

    const onCreate = () => {
        console.log('ReportOptionsDialog onCreate()');
    };

    const OKClicked = async () => {
        let data = getValues();

        if (props?.parentType === 3) {
            // The report generation is an async SSRS round-trip - wait for it before closing so the
            // dialog stays up (with a spinner) instead of vanishing before the download starts.
            setLoading(true);
            try {
                await props?.handleBillingReport(data?.reportOptions);
            } finally {
                setLoading(false);
            }
        } else {
            props.handleReport(getValues('category'), getValues('subcategory'));
        }

        props.hideDialog();
    };

    const ExcelClicked = () => {
        console.log('Excel Clicked');
        let data = getValues();

        props.handleReport(getValues('category'), getValues('subcategory'));
        props.hideDialog();
        props.showDialog(DialogList.EXCEL_FILE);

        console.log(data);
    };

    return (
        <Dialog title="Report Options" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '25%' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <form style={{width: '85%'}}>
                    {payPeriods && props?.parentType === 3 && <div className="form-row" style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <label style={{ whiteSpace: 'nowrap', marginRight: '8.7rem' }}>Pay Period:</label>
                        <div style={{ width: '70%' }}>
                            <Controller
                                name="reportOptions.payPeriod"
                                control={control}
                                defaultValue={null}
                                rules={{ required: 'Pay period required' }}
                                render={({ onChange, value }) => (
                                    <Select
                                        className="report-options-pay-period-select"
                                        placeholder="Select Pay Period"
                                        options={payPeriods}
                                        value={value ? (payPeriods.find(o => o.value === value) ?? null) : null}
                                        onMenuOpen={() => setIsSearchingPayPeriods(true)}
                                        onMenuClose={() => setIsSearchingPayPeriods(false)}
                                        onBlur={() => setIsSearchingPayPeriods(false)}
                                        menuIsOpen={isSearchingPayPeriods}
                                        isSearchable={true}
                                        isClearable={true}
                                        filterOption={(option, inputValue) =>
                                            normalizePayPeriodInput(option.label).includes(normalizePayPeriodInput(inputValue))
                                        }
                                        onInputChange={handlePayPeriodSearchChanged}
                                        onChange={(opt: { value: number | string; label: string } | null) => onChange(opt?.value ?? null)}
                                    />
                                )}
                            />
                        </div>
                    </div>}
                    {props?.parentType === 3 && <RHFInputBox
                        labelStyle={{whiteSpace: 'nowrap', marginRight: '6.45rem'}}
                        label="Client Number"
                        labelOnLeft={true}
                        name={`reportOptions.clientNumber`}
                        type="number"
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '70%' }}
                    />}
                    {props?.parentType === 3 && <RHFInputBox
                        label="Project Number"
                        labelOnLeft={true}
                        labelStyle={{whiteSpace: 'nowrap', marginRight: '5.7rem'}}
                        name={`reportOptions.projectNumber`}
                        type="number"
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '70%' }}
                    />}
                    {props?.parentType === 3 && <RHFInputBox
                        label="Subcode Number"
                        labelOnLeft={true}
                        labelStyle={{whiteSpace: 'nowrap', marginRight: '4.6rem'}}
                        name={`reportOptions.sectionNumber`}
                        type="number"
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '70%' }}
                    />}
                    {props?.parentType === 3 && <RHFInputBox
                        label="Manager Number"
                        labelOnLeft={true}
                        labelStyle={{whiteSpace: 'nowrap', marginRight: '4.5rem'}}
                        name={`reportOptions.managerNumber`}
                        type="number"
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '70%' }}
                    />}
                    {props?.parentType === 3 && <RHFCheckbox
                        label="Extended Summary?"
                        labelOnLeft={true}
                        labelStyle={{whiteSpace: 'nowrap', marginRight: '4.5rem'}}
                        name={`reportOptions.extended`}
                        register={register}
                        errors={errors}
                        bodyStyle={{ width: '100%' }}
                    />}
                    {props?.parentType === 3 && <RHFInputBox
                        label="APM"
                        labelOnLeft={true}
                        labelStyle={{whiteSpace: 'nowrap', marginRight: '9rem'}}
                        name={`reportOptions.ccNo`}
                        type="number"
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '70%' }}
                    />}
                    {props?.parentType === 3 && <RHFSelect
                        label="Originating Office"
                        labelOnLeft={true}
                        labelStyle={{whiteSpace: 'nowrap', marginRight: '4.5rem'}}
                        name={`reportOptions.offices`}
                        options={!!props?.offices && props?.offices?.length > 0 ? props.offices : [
                            { value: '1', label: 'OK' },
                            { value: '2', label: 'TX' },
                            { value: '3', label: 'IH' }
                        ]}
                        register={register}
                        errors={errors}
                        style={{ width: '70%' }}
                        bodyStyle={{ width: '100%' }}
                    />}
                    {props?.parentType === 3 && <RHFSelect
                        label="Work Description Format"
                        labelOnLeft={true}
                        labelStyle={{whiteSpace: 'nowrap'}}
                        name={`reportOptions.wdFormat`}
                        options={[
                            { value: '1', label: 'Detailed' },
                            { value: '2', label: 'Compact' },
                            { value: '3', label: 'None' }
                        ]}
                        register={register}
                        errors={errors}
                        style={{ width: '70%' }}
                        bodyStyle={{ width: '100%' }}
                    />}
                    <RHFSelect
                        label="Report Type"
                        labelOnLeft={true}
                        labelStyle={{whiteSpace: 'nowrap', marginRight: '8rem'}}
                        name={`reportOptions.reportType`}
                        options={!!props?.reportTypes ? props.reportTypes : [
                            { value: '1', label: 'Other Parameters - Invoice Date' },
                        ]}
                        register={register}
                        errors={errors}
                        style={{ width: '90%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    {(!!props?.sortOptions && props.sortOptions.length > 0 && props?.parentType === 2) && <RHFSelect
                        label="Sort Options"
                        labelOnLeft={true}
                        labelStyle={{ width: '15rem' }}
                        name={`reportOptions.sortOptions`}
                        options={props?.sortOptions}
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                    />}
                    {props?.parentType !== 2 && props?.parentType !== 3 && <RHFInputBox
                        label="Vendor Number"
                        labelOnLeft={true}
                        labelStyle={{ width: '15rem'}}
                        name={`reportOptions.vendorNumber`}
                        type="number"
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                        disabled={true}
                    />}
                    {props?.parentType !== 2 && props?.parentType !== 3 && <RHFInputBox
                        label="Beginning Date"
                        labelOnLeft={true}
                        labelStyle={{ width: '15rem'}}
                        name={`reportOptions.beginDate`}
                        type="date"
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                        disabled={true}
                    />}
                    {props?.parentType !== 2 && props?.parentType !== 3 && <RHFInputBox
                        label="Ending Date"
                        labelOnLeft={true}
                        labelStyle={{ width: '15rem'}}
                        name={`reportOptions.endDate`}
                        type="date"
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                        disabled={true}
                    />}
                </form>
            </div>
            <div className="form-row" style={{marginTop: '2rem', justifyContent: 'center'}}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={loading ? 'Generating' : 'OK'}
                    disabled={loading}
                    loading={loading}
                    onClick={() => !loading && OKClicked()}
                />
                {props?.parentType !== 2 && props?.parentType !== 3 && <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'Excel'}
                    onClick={() => ExcelClicked()}
                />}
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'Cancel'}
                    disabled={loading}
                    onClick={() => props.hideDialog()}
                />
            </div>
        </Dialog>
    );
};

export default ReportOptionsDialog;



// WEBPACK FOOTER //
// ./src/components/accounts-payable/dialogs/ReportOptionsDialog.tsx